/**
 * Perplexity Quick Wins: /resources/perplexity/quick-wins
 *
 * The ten Quick Wins from Caren's Perplexity PDF, as a page rather than a
 * download. Same component shape as the Gemini Notebook and Littlebird Quick
 * Wins pages — a WinCard list grouped in threes, opening one at a time — so
 * the three read as one series rather than three attempts at the same idea.
 *
 * The content is the PDF's, close to verbatim. The wins are the deliverable;
 * rewriting them here would put two versions of Caren's words in circulation
 * and there would be no way to tell which one was current.
 */

import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link } from "wouter";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};

// ── Data ─────────────────────────────────────────────────────────────────────
interface QuickWin {
  num: number;
  title: string;
  when: string;
  how: string[];
  principle: string;
}

const WINS: QuickWin[] = [
  {
    num: 1,
    title: "Use Perplexity Instead of Google for First-Pass Research",
    when: "Use this when you need orientation, not depth.",
    how: [
      "Ask a clear, plain-language question",
      "Read the synthesized answer: citations included",
      "Scan one source only if you need to verify a key claim",
      "Stop before opening multiple tabs",
    ],
    principle: "Orientation comes before exploration.",
  },
  {
    num: 2,
    title: "Turn On Pro Search for Complex Questions",
    when: "Use this when a simple search isn't giving you enough.",
    how: [
      "Click the Pro Search toggle before submitting your query",
      "Let Perplexity run multi-step reasoning across several sources",
      "Read the synthesized answer, then check the cited sources",
      "Use Pro Search selectively: it's more thorough, not faster",
    ],
    principle: "Depth on demand, not depth by default.",
  },
  {
    num: 3,
    title: "Use Deep Research for Comprehensive Source Gathering",
    when: "Use this when you need more than a quick answer.",
    how: [
      "Select Deep Research mode from the search bar",
      "Perplexity will run an extended multi-source investigation",
      "You'll receive an email notification when the report is ready",
      "Review the full report, then bring key insights into your thinking tool",
    ],
    principle: "A strong source library is the foundation of clear thinking.",
  },
  {
    num: 4,
    title: "Build a Space for Ongoing Research",
    when: "Use this when a topic or project spans multiple sessions.",
    how: [
      "Create a Space from the left sidebar",
      "Upload relevant files, PDFs, or link specific websites as grounding sources",
      "Ask questions inside the Space: answers stay anchored to your materials",
      "Add custom AI instructions to set the role and focus for that Space",
    ],
    principle: "Context stored is context you don't have to rebuild.",
  },
  {
    num: 5,
    title: "Set Up a Scheduled Task to Monitor What Matters",
    when: "Use this when you manually check the same topics each week.",
    how: [
      "Go to a Space and click Create Task",
      "Write a clear prompt for what you want monitored: a competitor, a topic, a trend",
      "Set a schedule (daily, weekly, or weekday-only)",
      "Perplexity runs the search without you and emails the summary",
    ],
    principle: "A well-set task runs while you think about something else.",
  },
  {
    num: 6,
    title: "Validate Assumptions Without Spiraling",
    when: "Use this when you want to sanity-check a belief before acting on it.",
    how: [
      "State the assumption clearly in your query",
      "Ask whether general evidence supports it",
      "Look for consensus, not certainty",
      "Decide how much weight to give it, then move on",
    ],
    principle: "Assumptions deserve a quick check, not a full investigation.",
  },
  {
    num: 7,
    title: "Use the Model Council to Cross-Check Important Answers",
    when: "Use this when a decision is high-stakes and one answer isn't enough.",
    how: [
      "Enable Model Council from the search settings",
      "Perplexity routes your query through multiple frontier AI models simultaneously",
      "Compare where models agree and where they diverge",
      "Use disagreement as a signal to look closer, not a reason to delay",
    ],
    principle: "Contrast sharpens clarity.",
  },
  {
    num: 8,
    title: "Upload Files and Ask Questions Across Them",
    when: "Use this when key information lives in documents you already have.",
    how: [
      "Upload PDFs, spreadsheets, or reports directly into a Space or query",
      "Ask questions that connect your files to live web research",
      "Ask for summaries by section or theme rather than full reads",
      "Save only the insights that change what you'll do",
    ],
    principle: "Your files deserve the same research treatment as the web.",
  },
  {
    num: 9,
    title: "Use Perplexity With Your Thinking Tool, Not Instead of It",
    when: "Use this when you're unsure which AI tool to open.",
    how: [
      "Use Perplexity to gather context, citations, and grounded facts",
      "Bring distilled insights into your reasoning tool for deeper thinking",
      "Keep the roles separate: one retrieves, one reasons",
      "Don't stay in Perplexity longer than the research requires",
    ],
    principle: "Different tools serve different cognitive jobs.",
  },
  {
    num: 10,
    title: "Stop Research at “Enough”",
    when: "Use this when research keeps expanding and decisions keep waiting.",
    how: [
      "Define what “enough” means before you start searching",
      "Watch for repeated themes across sources: that's your signal",
      "Stop once patterns appear",
      "Move on: decide with what you have",
    ],
    principle: "Research serves decisions, not the other way around.",
  },
];

const GROUPS = [
  { label: "Quick Wins 1 – 3", wins: WINS.slice(0, 3) },
  { label: "Quick Wins 4 – 6", wins: WINS.slice(3, 6) },
  { label: "Quick Wins 7 – 10", wins: WINS.slice(6, 10) },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{
        transition: "transform 0.25s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
        color: C.muted,
      }}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Win card ──────────────────────────────────────────────────────────────────
function WinCard({ win, defaultOpen = false }: { win: QuickWin; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${open ? C.terracottaMid : C.border}`,
        borderLeft: `3px solid ${open ? C.terracotta : C.terracottaMid}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? "0 6px 24px oklch(0.32 0.08 255 / 0.10)"
          : hovered
          ? "0 2px 10px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.4rem",
          background: open ? C.terracottaLight : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.2s ease",
        }}
      >
        <span
          style={{
            width: 32, height: 32, minWidth: 32,
            borderRadius: "50%",
            background: open ? C.terracotta : C.terracottaLight,
            color: open ? C.white : C.terracotta,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 13, fontWeight: 700,
            transition: "background 0.2s ease, color 0.2s ease",
            flexShrink: 0,
          }}
        >
          {win.num}
        </span>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 16, fontWeight: 600, color: C.espresso,
            flex: 1, lineHeight: 1.35,
          }}
        >
          {win.title}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          style={{
            padding: "1.25rem 1.4rem 1.5rem",
            borderTop: `1px solid ${C.terracottaMid}`,
            background: C.white,
          }}
        >
          <div style={{ marginBottom: "1.1rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.35rem" }}>
              When This Helps
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", lineHeight: 1.7 }}>
              {win.when}
            </p>
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.65rem" }}>
              How
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {win.how.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 20, height: 20, minWidth: 20,
                      borderRadius: "50%",
                      background: C.linen,
                      border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 10, fontWeight: 700, color: C.muted,
                      marginTop: 2, flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderLeft: `3px solid ${C.terracotta}`,
              borderRadius: 6,
              padding: "0.75rem 1rem",
            }}
          >
            <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, display: "block", marginBottom: "0.3rem" }}>
              Smart Stack Principle
            </span>
            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 14, color: C.espresso, fontStyle: "italic", lineHeight: 1.5 }}>
              {win.principle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PerplexityQuickWinsPage() {
  return (
    <GuideShell
      context={"Perplexity guide"}
      backHref={"/resources/perplexity"}
      backLabel={"Back to Perplexity"}
    >

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "3.5rem 1.5rem 5rem" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "3rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${C.border}` }}>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11, fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracotta,
              marginBottom: "1rem",
            }}
          >
            Your Smart Stack Quick Wins
          </p>
          <h1
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              fontWeight: 700,
              color: C.espresso,
              lineHeight: 1.2,
              marginBottom: "0.75rem",
            }}
          >
            Perplexity
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 18,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Get oriented. Stop at enough.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 20,
              padding: "0.45rem 1.1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 12, fontWeight: 600,
                color: C.terracotta,
                letterSpacing: "0.04em",
              }}
            >
              Small moves. Clear thinking. Less friction.
            </span>
          </div>
        </div>

        {/* Intro card */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "1.75rem 2rem",
            marginBottom: "2.75rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.3rem" }}>
            Get oriented fast.
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem" }}>
            Research without getting lost.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.9rem" }}>
            Perplexity is most useful as a first-pass research and orientation tool, not a place to camp. Ask a plain-language question, read the cited answer, verify what matters, and move on.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.5rem" }}>
            This guide shows how to use it to gather context, validate assumptions, and stop at “enough”, so research supports decisions instead of delaying them.
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: C.terracotta, marginBottom: "0.6rem" }}>
              How to Use This Guide
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 16, fontWeight: 600, color: C.espresso, marginBottom: "0.5rem" }}>
              This is not a checklist.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginBottom: "0.75rem" }}>
              You don’t need to apply everything here. Each Quick Win is designed for moments when:
            </p>
            {[
              "research starts to sprawl",
              "curiosity turns into avoidance",
              "information adds pressure instead of clarity",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginBottom: "0.45rem" }}>
                <span style={{ color: C.terracotta, fontWeight: 700, fontSize: 15, flexShrink: 0, lineHeight: 1.6 }}>–</span>
                <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.7, margin: 0 }}>{item}</p>
              </div>
            ))}
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 14, color: C.body, lineHeight: 1.75, marginTop: "0.85rem" }}>
              Start with the Quick Win that helps you stop sooner. Skip the rest.
            </p>
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, fontWeight: 600, color: C.espresso, marginTop: "0.6rem", fontStyle: "italic" }}>
              Research should lighten thinking, not replace it.
            </p>
          </div>
        </div>

        {/* Quick Win groups */}
        {GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: "2.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.1rem" }}>
              <div style={{ height: 1, flex: 1, background: C.border }} />
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 10, letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: C.terracotta,
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </p>
              <div style={{ height: 1, flex: 1, background: C.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              {group.wins.map((win, i) => (
                <WinCard
                  key={win.num}
                  win={win}
                  defaultOpen={i === 0 && group.wins[0].num === 1}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Closing note */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "2rem 2.25rem",
            marginBottom: "2.5rem",
          }}
        >
          <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: C.espresso, marginBottom: "0.5rem" }}>
            A Note on Research Discipline
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.muted, fontStyle: "italic", marginBottom: "1.1rem", lineHeight: 1.7 }}>
            Perplexity works best when it helps you arrive, not wander.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.5rem" }}>
            It’s a tool for context, validation, and orientation. It’s not a substitute for thinking or deciding.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.25rem" }}>
            Use it to get grounded. Then close it.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1.75rem" }}>
            {["Orient quickly.", "Stop early.", "Trust your judgment."].map((line) => (
              <p
                key={line}
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16, fontWeight: 600,
                  color: C.espresso,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {line}
              </p>
            ))}
          </div>
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 8,
              padding: "1.1rem 1.4rem",
            }}
          >
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 15, color: C.espresso, fontStyle: "italic", lineHeight: 1.65, marginBottom: "0.6rem" }}>
              “Perplexity is how I get oriented, without getting lost.”
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 700, color: C.terracotta, marginBottom: "0.1rem" }}>
              Your Smart Stack
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
              Your Smart Stack
            </p>
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            href="/resources/perplexity"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.muted,
              fontWeight: 600,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: C.white,
              transition: "border-color 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.terracotta;
              e.currentTarget.style.color = C.terracotta;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.muted;
            }}
          >
            ← Perplexity Resources
          </Link>
          <a
            href="https://pplx.ai/carenglasser"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13, color: C.white,
              fontWeight: 700,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              background: C.terracotta,
              borderRadius: 8,
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Try Perplexity →
          </a>
        </div>
      </main>


      <style>{`
        @media (max-width: 600px) {
          main { padding: 2rem 1rem 4rem !important; }
        }
      `}</style>
    </GuideShell>
  );
}
